/**
 * Lời gọi API cho nhóm màn hình lịch phát và điều hành phát: MH-19 đến MH-23,
 * đúng hình dạng của `apps/api/src/phat-song/phat-song.controller.ts`.
 */
import { chuoiTruyVan, goi, taiTepTuApi } from './goi-chung';
import type { DiaBanNhanDV } from './lich-phat-api';
import type { TrangDuLieu } from './thiet-bi-api';

export interface PhamViPhatVao {
  banTinId?: string;
  phamVi: DiaBanNhanDV[];
  lyDo?: string;
  /** CumLoaID các thiết bị đã tích chọn; không có = phát toàn bộ địa bàn. */
  cumLoaId?: string[];
}

/** Bản kê hệ quả định lượng — cổng nghiệm thu bắt buộc của MH-19 và MH-20. */
export interface BanKeHeQuaKhanCap {
  soCumLoaTrongPhamVi: number;
  soCumDangPhatNoiDungKhac: number;
  soLichPhatSeBiHuy: number;
  soCumKetNoiGianTiep: number;
  lichSeBiHuy: { id: string; tenLichPhat: string }[];
}

export interface LoiThietBiPhiHanh {
  cumLoaId: string;
  maLoi: string;
  thongBao: string;
}

/** Ba nhóm thiết bị của MH-23 theo dõi phiên phát. */
export interface BaNhomPhienPhat {
  phienId: string;
  banTinId: string | null;
  banTinMa: string | null;
  topic: string[];
  soThietBiUocTinh: number;
  thoiDiemGui: number | null;
  daXacNhan: { soLuong: number; danhSach?: string[] };
  choXacNhan: { soLuong: number; danhSach?: string[] };
  loi: { soLuong: number; danhSach: LoiThietBiPhiHanh[] };
}

export interface DongNgheLai {
  id: string;
  banTinId: string | null;
  banTinMa: string | null;
  tieuDe: string | null;
  loaiLinhVuc: string | null;
  cumLoaId: string;
  nguonId: string;
  lichPhatMa: string | null;
  thoiDiemBatDau: number;
  thoiDiemKetThuc: number | null;
  ketQua: string;
  nguonKichHoat: string;
  /** Thời lượng bản tin dạng HH:mm:ss; tuỳ chọn để tương thích máy chủ cũ. */
  thoiLuong?: string | null;
  /** Mức độ ưu tiên '1' khẩn cấp, '2' ưu tiên, '3' thông thường. */
  mucDoUuTien?: string | null;
  /** Loại bản tin '1'..'5'. */
  loaiBanTin?: string | null;
  /** Tên địa bàn phát, đã viết hoa chữ đầu. */
  tenDiaBan?: string | null;
}

export interface BoLocNgheLai {
  page?: number;
  pageSize?: number;
  keyword?: string;
  nguonId?: string;
  cumLoaId?: string;
  tuThoiDiem?: string;
  denThoiDiem?: string;
  ketQua?: string;
  /** Mã loại bản tin '1'..'5'. */
  loaiBanTin?: string;
  /** Mã mức độ ưu tiên '1'..'3'. */
  mucDoUuTien?: string;
  /** Mã loại lĩnh vực '1'..'6'. */
  loaiLinhVuc?: string;
}

/**
 * Một dòng nhật ký lịch phát thiết bị: gói DV006 thiết bị/bộ tương thích báo đã
 * nhận lịch phát — đúng hợp đồng A0 của `GET /api/v1/broadcast/schedule-logs`.
 */
export interface DongNhatKyLichThietBi {
  id: string;
  lichPhatId: string;
  tenLichPhat: string | null;
  cumLoaId: string;
  tenThietBi: string | null;
  maDiaBan: string | null;
  tenDiaBan: string | null;
  soBanTin: number | null;
  /** Epoch giây. */
  thoiDiemGui: number;
}

export interface BoLocNhatKyLichThietBi {
  page?: number;
  pageSize?: number;
  nguonId?: string;
  /** Tìm theo tên hoặc mã lịch. */
  keyword?: string;
  /** Epoch giây, 00:00 giờ Việt Nam của ngày bắt đầu. */
  tuThoiDiem?: number;
  /** Epoch giây, 24:00 giờ Việt Nam của ngày kết thúc. */
  denThoiDiem?: number;
}

/** MH-21 nguồn tiếp sóng/tiếp âm, đúng hình dạng của `nguon-tiep-song.service.ts`. */
export interface DongNguonTiepSong {
  id: string;
  ten: string;
  loai: 'hls' | 'icy' | 'rtmp' | 'fm';
  diaChi: string;
  phamViApDung: string[] | null;
  trangThai: string;
  dangThu: boolean;
  mucAmLuongDb: number | null;
  imLang: boolean | null;
  capNhatLuc: number | null;
}

export interface NguonTiepSongVao {
  ten: string;
  loai: 'hls' | 'icy' | 'rtmp' | 'fm';
  diaChi: string;
  phamViApDung?: string[];
}

export const phatSongApi = {
  /* ------------------------------- Phát ngay ------------------------------- */
  phatNgay: (than: PhamViPhatVao): Promise<{ phienId: string }> =>
    goi('/api/v1/broadcast/now', { phuongThuc: 'POST', than }),

  dungPhat: (than: PhamViPhatVao & { hanhDong: string }): Promise<unknown> =>
    goi('/api/v1/broadcast/stop', { phuongThuc: 'POST', than }),

  /* ----------------------------- Phát khẩn cấp ----------------------------- */
  khaoSatHeQua: (phamVi: DiaBanNhanDV[]): Promise<BanKeHeQuaKhanCap> =>
    goi('/api/v1/broadcast/emergency/impact', { phuongThuc: 'POST', than: { phamVi } }),

  phatKhanCap: (than: PhamViPhatVao & { soLanLap?: number }): Promise<{ phienId: string }> =>
    goi('/api/v1/broadcast/emergency', { phuongThuc: 'POST', than }),

  /* ------------------------- Phát trực tiếp qua micro ------------------------ */
  // `phienId` là mã bản ghi phiên (dùng để đóng phiên); `phienKenh` là mã phiên
  // ở tầng kênh phát, DÙNG ĐỂ THEO DÕI ở MH-23 (hai mã khác nhau — xem
  // `phat-song.service.ts` mục `moPhienMicro`, đã ghi vào NOTES-A0.md).
  moPhienMicro: (
    phamVi: DiaBanNhanDV[],
  ): Promise<{ phienId: string; phienKenh: string; duongDanGhiLuu: string }> =>
    goi('/api/v1/broadcast/live/start', { phuongThuc: 'POST', than: { phamVi } }),

  dongPhienMicro: (phienId: string): Promise<unknown> =>
    goi('/api/v1/broadcast/live/stop', { phuongThuc: 'POST', than: { phienId } }),

  /* ---------------------------------- Tiếp sóng ---------------------------- */
  batDauTiepSong: (phamVi: DiaBanNhanDV[], nguonTiepSongId: string): Promise<{ phienId: string }> =>
    goi('/api/v1/broadcast/relay/start', { phuongThuc: 'POST', than: { phamVi, nguonTiepSongId } }),

  dungTiepSong: (phienId: string): Promise<unknown> =>
    goi('/api/v1/broadcast/relay/stop', { phuongThuc: 'POST', than: { phienId } }),

  giamSat: (cumLoaId: string, signal?: AbortSignal): Promise<unknown> =>
    goi(`/api/v1/broadcast/live-monitor${chuoiTruyVan({ cumLoaId })}`, { signal }),

  /* ------------------------- Theo dõi phiên, nghe lại ------------------------ */
  theoDoiPhien: (phienId: string, signal?: AbortSignal): Promise<BaNhomPhienPhat> =>
    goi(`/api/v1/broadcast/sessions/${phienId}`, { signal }),

  /** Xuất Excel (.xlsx) nhật ký bản tin đã phát theo bộ lọc (không gồm phân trang). */
  xuatExcelNgheLai: (tham: BoLocNgheLai): Promise<{ soDong: number; tong: number }> =>
    taiTepTuApi(
      `/api/v1/broadcast/logs/export${chuoiTruyVan(tham as Record<string, string | number | undefined>)}`,
      'nhat-ky-ban-tin-da-phat.xlsx',
    ),

  /** Xuất Excel (.xlsx) nhật ký lịch phát thiết bị theo bộ lọc (không gồm phân trang). */
  xuatExcelNhatKyLich: (tham: BoLocNhatKyLichThietBi): Promise<{ soDong: number; tong: number }> =>
    taiTepTuApi(
      `/api/v1/broadcast/schedule-logs/export${chuoiTruyVan(tham as Record<string, string | number | undefined>)}`,
      'nhat-ky-lich-phat-thiet-bi.xlsx',
    ),

  ngheLai: (tham: BoLocNgheLai, signal?: AbortSignal): Promise<TrangDuLieu<DongNgheLai>> =>
    goi(`/api/v1/broadcast/logs${chuoiTruyVan(tham as Record<string, string | number | undefined>)}`, {
      signal,
    }),

  nhatKyLichThietBi: (
    tham: BoLocNhatKyLichThietBi,
    signal?: AbortSignal,
  ): Promise<TrangDuLieu<DongNhatKyLichThietBi>> =>
    goi(
      `/api/v1/broadcast/schedule-logs${chuoiTruyVan(tham as Record<string, string | number | undefined>)}`,
      { signal },
    ),

  ngheLaiMot: (id: string, signal?: AbortSignal): Promise<unknown> =>
    goi(`/api/v1/broadcast/logs/${id}/playback`, { signal }),

  /* --------------------------------- Tiếp sóng danh mục --------------------- */
  danhSachNguonTiepSong: (signal?: AbortSignal): Promise<unknown> =>
    goi('/api/v1/catalogs/relay-sources', { signal }),

  khaiBaoNguonTiepSong: (than: Record<string, unknown>): Promise<unknown> =>
    goi('/api/v1/catalogs/relay-sources', { phuongThuc: 'POST', than }),
};
